import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import { useContext } from "react";
import { TokenContext } from "../../../context/TokenContext";
import FileUpload from "../../common/FileUpload";
import { useProfile } from "../../../context/ProfileContext";
import { httpService } from "../../../services/httpService";
 
// Memoize the FormField component
const FormField = React.memo(
  ({
    label,
    required,
    error,
    children,
    labelCheckBoxNode = "",
    classApplied = "",
  }) => (
    <div className={`space-y-2 ${classApplied}`}>
      <label className="block">
        {label} {required && <span className="text-red-500">*</span>}
        {labelCheckBoxNode}
      </label>
      {children}
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  )
);
 
FormField.displayName = "FormField";
 
const ServiceForm = ({
  isActive,
  serviceId: propServiceId,
  // setServiceCategory,
  data,
  updateData,
  onValidationChange,
  isNoSubcategoryPlan, 
}) => {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isCheckingName, setIsCheckingName] = useState(false);

  const { rawServiceCategory } = useProfile();
  const isActiveFlag = (st) =>
    st && (st.is_active === 1 || st.is_active === "1" || st.is_active === true);

  const { isFreePlan, isHomePlan, isShopPlan } = useContext(TokenContext);

  const [categories] = useState(
    rawServiceCategory
      .filter(
        (cat) => cat.type_id === 1 && cat.service_category_name === "Grooming",
      )
      .map((cat) => ({
        ...cat,
        service_type: Array.isArray(cat.service_type)
          ? cat.service_type.filter(isActiveFlag)
          : [],
      }))
      .filter((cat) => cat.service_type.length > 0),
  );

  // Pet Types State
  const [petTypes, setPetTypes] = useState([]);
  const [selectedPetTypes, setSelectedPetTypes] = useState([]); // Full objects
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [loadingPetTypes, setLoadingPetTypes] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    ...data,
    taxable: data.tax_percentage > 0 ? "yes" : "no",
    taxPercentage: data.tax_percentage?.toString() || "",
    pet_type: data.pet_type || [],
  });
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const petTypesLoadedRef = useRef(false);

  // Load pet types
  const loadPetTypes = async () => {
    setLoadingPetTypes(true);
    try {
      const res = await httpService.get("/vendors/getPetTypes?isEnabled=true");
      const list = res.data?.data ?? res.data ?? [];
      setPetTypes(list);
    } catch (err) {
      console.error("Error loading pet types:", err);
      toast.error("Failed to load pet types.");
    } finally {
      setLoadingPetTypes(false);
    }
  };
  const isInteractingRef = useRef(false);

  useEffect(() => {
    if (!isActive) return;

    setForm((prev) => ({
      ...prev,
      ...data,
      category: propServiceId ? data.category : prev.category,
      taxable:
        data.taxable !== undefined
          ? data.taxable
          : data.tax_percentage > 0
            ? "yes"
            : "no",
      taxPercentage:
        data.taxPercentage !== undefined
          ? data.taxPercentage
          : data.tax_percentage?.toString() || "",
      pet_type: data.pet_type || [],
    }));

    // ✅ Load pet types only once
    if (petTypesLoadedRef.current) return;
    petTypesLoadedRef.current = true;
    loadPetTypes();
  }, [data, isActive, propServiceId]);

  // Track initialization status
  const isPetTypeInitialized = useRef(false);

  // Sync selected pet types to form (IDs only)
  useEffect(() => {
    // Prevent syncing empty state before initialization is complete
    if (!isPetTypeInitialized.current && selectedPetTypes.length === 0) return;

    const petTypeIds = selectedPetTypes.map((pt) => pt.pet_type_id);
    setForm((prev) => ({ ...prev, pet_type: petTypeIds }));
  }, [selectedPetTypes]);

  // FIXED: Handle both pet_type formats (array of IDs OR array of objects)
  useEffect(() => {
    if (isActive && petTypes.length > 0 && data.pet_type) {
      const preSelected = petTypes.filter((pt) =>
        (data.pet_type || []).some(
          (pet) =>
            // Check for both formats
            pet.pet_type_id === pt.pet_type_id ||
            pet.id === pt.pet_type_id ||
            (typeof pet === "string" && pet === pt.pet_type_id),
        ),
      );
      setSelectedPetTypes(preSelected);
      isPetTypeInitialized.current = true; // Mark as initialized
    }
  }, [petTypes, data.pet_type, isActive]);

  // Reset when creating new service
  useEffect(() => {
    if (!propServiceId) {
      setSelectedPetTypes([]);
    }
  }, [propServiceId]);

  // Select All logic
  useEffect(() => {
    if (petTypes.length > 0 && selectedPetTypes.length === petTypes.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedPetTypes, petTypes]);

  // Default category logic
  // useEffect(() => {
  //   if (isFreePlan && !data.category?.service_category_type_id) {
  //     let defaultCategory = null;
  //     const groomingCategory = categories?.find(
  //       (cat) => cat.type_id === 1 && cat.service_category_name === "Grooming"
  //     );
  //     if (groomingCategory) {
  //       const homeGrooming = groomingCategory.service_type.find((st) =>
  //         st.service_type_name.toLowerCase().includes("home grooming")
  //       );
  //       if (homeGrooming) {
  //         defaultCategory = {
  //           service_category_id: groomingCategory.service_category_id,
  //           service_type_id: homeGrooming.service_type_id,
  //           service_type_name: homeGrooming.service_type_name,
  //           service_category_type_id: homeGrooming.service_category_type_id,
  //           type_id: groomingCategory.type_id,
  //           service_category_name: groomingCategory.service_category_name,
  //         };
  //         setServiceCategory(defaultCategory);
  //         setForm((prev) => ({ ...prev, category: defaultCategory }));
  //       }
  //     }
  //   } else if (isShopPlan && !data.category?.service_category_type_id) {
  //     let defaultCategory = null;
  //     const groomingCategory = categories?.find(
  //       (cat) => cat.type_id === 1 && cat.service_category_name === "Grooming"
  //     );
  //     if (groomingCategory) {
  //       const groomingSalon = groomingCategory.service_type.find((st) =>
  //         st.service_type_name.toLowerCase().includes("grooming salon")
  //       );
  //       if (groomingSalon) {
  //         defaultCategory = {
  //           service_category_id: groomingCategory.service_category_id,
  //           service_type_id: groomingSalon.service_type_id,
  //           service_type_name: groomingSalon.service_type_name,
  //           service_category_type_id: groomingSalon.service_category_type_id,
  //           type_id: groomingCategory.type_id,
  //           service_category_name: groomingCategory.service_category_name,
  //         };
  //         setServiceCategory(defaultCategory);
  //         setForm((prev) => ({ ...prev, category: defaultCategory }));
  //       }
  //     }
  //   }
  // }, [
  //   isFreePlan,
  //   isHomePlan,
  //   isShopPlan,
  //   categories,
  //   data.category,
  //   // setServiceCategory,
  // ]);
 
  // Name check API
  const checkServiceName = useCallback(async (serviceName, serviceType) => {
    if (serviceName.trim().length < 3) return;
    setIsCheckingName(true);
    try {
      const payload = { serviceName, serviceType: serviceType.toLowerCase() };
      const response = await httpService.post(
        "/vendorservice/serviceNameCheck",
        payload,
      );
      if (response.message === "Service name available.") {
        setNameError(null);
      } else {
        setNameError(
          "Service Name already exists. Please choose a different name.",
        );
        toast.error("Service Name already exists.");
      }
    } catch (err) {
      const apiError =
        err.response?.data?.message || "Error checking service name.";
      setNameError(apiError);
      toast.error(apiError);
    } finally {
      setIsCheckingName(false);
    }
  }, []);

  const debouncedCheckName = useMemo(
    () => debounce((name, type) => checkServiceName(name, type), 500),
    [checkServiceName],
  );

  const validate = useCallback(
    (field, value) => {
      const newErrors = { ...errors };
 
      if (field === "name") {
        if (!value?.trim()) newErrors.name = "Service Name is required";
        else if (value.length < 3)
          newErrors.name = "Name must be at least 3 characters";
        else delete newErrors.name;
      }
 
      // if (!isFreePlan && field === "category") {
      //   if (!value?.service_category_type_id)
      //     newErrors.category = "Service Category is required";
      //   else delete newErrors.category;
      // }
 
      if (field === "flatDurationMinutes") {
        if (
          !form.flatDurationMinutes ||
          Number(form.flatDurationMinutes) <= 0
        ) {
          newErrors.flatDurationMinutes = "Please select a non-zero duration";
        } else delete newErrors.flatDurationMinutes;
      }

      if (field === "basicPrice") {
        if (value === "") newErrors.basicPrice = "Basic Price is required";
        else if (Number(value) === 0)
          newErrors.basicPrice = "Basic Price cannot be zero";
        else delete newErrors.basicPrice;
      }

      if (field === "pet_type") {
        if (selectedPetTypes.length === 0)
          newErrors.pet_type = "Pet Type is required";
        else delete newErrors.pet_type;
      }

      if (field === "taxPercentage") {
        if (form.taxable === "yes" && (!value || Number(value) <= 0)) {
          newErrors.taxPercentage =
            "Service Charges is required when Seperate Service Charges is yes";
        } else delete newErrors.taxPercentage;
      }

      setErrors(newErrors);
      const isValid = Object.keys(newErrors).length === 0 && !nameError;
      onValidationChange?.(isValid);
      return isValid;
    },
    [errors, form, onValidationChange, nameError, selectedPetTypes.length],
  );

  const debouncedUpdateData = useMemo(
    () => debounce((fd) => updateData(fd), 300),
    [updateData],
  );

  useEffect(() => {
    debouncedUpdateData(form);
    return () => debouncedUpdateData.cancel();
  }, [form, debouncedUpdateData]);

  const handleChange = useCallback(
    (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      validate(field, value);
    },
    [validate],
  );
 
  // const handleCategoryChange = useCallback(
  //   (value, e) => {
  //     const ds = e.target.selectedOptions[0].dataset;
  //     const sd = {
  //       service_category_id: ds.servicecategoryid,
  //       service_type_id: ds.servictypeid,
  //       service_type_name: ds.subcatname,
  //       service_category_type_id: value,
  //       type_id: ds.typeid,
  //       service_category_name: ds.catname,
  //     };
  //     setServiceCategory(sd);
  //     setForm((prev) => ({ ...prev, category: sd }));
  //     setTouched((prev) => ({ ...prev, category: true }));
  //     validate("category", sd);
  //   },
  //   [setServiceCategory, validate]
  // );
 
  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      validate(field, form[field]);
      if (
        field === "name" &&
        form.name?.trim().length >= 3 &&
        form.category?.service_category_name
      ) {
        debouncedCheckName(form.name, form.category.service_category_name);
      }
    },
    [validate, form, debouncedCheckName],
  );

  const handleDefaultTemplate = useCallback(() => {
    const template = {
      ...form,
      priceBasedOn: "flat",
      durationBasedOn: "flat",
      flatDurationMinutes: 30,
      basicPrice: 1000,
      taxable: "yes",
      taxPercentage: "5",
    };
    setForm(template);
    updateData(template);
    toast.success("Default template applied successfully");
  }, [form, updateData]);

  // Pet Type Handlers
  const togglePetType = (petType) => {
    setSelectedPetTypes((prev) => {
      const exists = prev.some((p) => p.pet_type_id === petType.pet_type_id);
      return exists
        ? prev.filter((p) => p.pet_type_id !== petType.pet_type_id)
        : [...prev, petType];
    });
  };

  const removePetType = (id) => {
    setSelectedPetTypes((prev) => prev.filter((p) => p.pet_type_id !== id));
  };

  const toggleSelectAll = () => {
    setSelectedPetTypes(selectAll ? [] : [...petTypes]);
  };

  useEffect(() => {
    if (!dropdownOpen) {
      isInteractingRef.current = false;
      validate("pet_type");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    setIsPageLoading(false);
  }, []);

  if (isPageLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4">Loading service details...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-medium">Basic Information</h3>
            {!isFreePlan && (
              <button
                type="button"
                onClick={handleDefaultTemplate}
                disabled={isCheckingName}
                className="p-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 h-[36px]"
              >
                Use Default Template
              </button>
            )}
          </div>
 
          {/* Service Name */}
          <div className="w-full mb-4">
            <FormField
              label="Service Name"
              required
              error={(touched.name && errors.name) || nameError}
            >
              <input
                type="text"
                value={form.name || ""}
                onChange={(e) => handleChange("name", e.target.value)}
                onBlur={() => handleBlur("name")}
                className={`block w-full p-2 border rounded ${
                  (touched.name && errors.name) || nameError
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                placeholder="Enter service name"
              />
            </FormField>
          </div>

          {/* Pet Type + Bookable Online */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
            {/* === FIXED PET TYPE DROPDOWN === */}
            <FormField
              label="Pet Type"
              required
              error={touched.pet_type && errors.pet_type}
              classApplied="relative z-[60]"
            >
              <div className="relative" ref={dropdownRef}>
                {/* INPUT */}
                <div
                  className="w-full px-3 py-2 border rounded bg-white min-h-[42px] flex flex-wrap items-center gap-2 cursor-pointer"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    isInteractingRef.current = true;
                    setDropdownOpen((prev) => !prev);
                  }}
                >
                  {selectedPetTypes.length === 0 ? (
                    <span className="text-gray-500">Select Pet Types</span>
                  ) : (
                    selectedPetTypes.map((pt) => (
                      <span
                        key={pt.pet_type_id}
                        className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full"
                      >
                        {pt.name}
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            removePetType(pt.pet_type_id);
                          }}
                          className="ml-1 hover:bg-green-200 rounded-full w-5 h-5 flex items-center justify-center"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}

                  <svg
                    className={`ml-auto w-5 h-5 transition-transform ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>

                {/* DROPDOWN */}
                {dropdownOpen && (
                  <div
                    className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {/* SELECT ALL */}
                    <div
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggleSelectAll();
                      }}
                    >
                      <span className="font-medium">
                        {selectAll ? "Unselect All" : "Select All"}
                      </span>
                    </div>

                    {loadingPetTypes ? (
                      <div className="px-4 py-3 text-gray-500">Loading...</div>
                    ) : petTypes.length === 0 ? (
                      <div className="px-4 py-3 text-gray-500">
                        No pet types found
                      </div>
                    ) : (
                      petTypes.map((pt) => {
                        const isSelected = selectedPetTypes.some(
                          (s) => s.pet_type_id === pt.pet_type_id,
                        );

                        return (
                          <div
                            key={pt.pet_type_id}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              togglePetType(pt);
                            }}
                          >
                            <div className="w-5 h-5 rounded border-2 flex items-center justify-center">
                              {isSelected && "✓"}
                            </div>
                            <span>{pt.name}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </FormField>

            {/* Bookable Online */}
            <FormField label="Bookable Online">
              <select
                value={form.bookable_online || "yes"}
                onChange={(e) =>
                  handleChange("bookable_online", e.target.value)
                }
                className="block w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </FormField>
          </div>

          {/* Duration */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
            <FormField
              label="Duration (minutes)"
              required
              error={touched.flatDurationMinutes && errors.flatDurationMinutes}
            >
              <select
                value={form.flatDurationMinutes || ""}
                onChange={(e) =>
                  handleChange("flatDurationMinutes", Number(e.target.value))
                }
                onBlur={() => handleBlur("flatDurationMinutes")}
                className={`block w-full p-2 border rounded ${
                  touched.flatDurationMinutes && errors.flatDurationMinutes
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select duration</option>
                {Array.from({ length: 12 }, (_, i) => (i + 1) * 15).map(
                  (minutes) => (
                    <option key={minutes} value={minutes}>
                      {minutes} minutes
                    </option>
                  ),
                )}
              </select>
            </FormField>
          </div>

          {/* Basic Price */}
          <div className="w-full mb-4">
            <FormField
              label="Basic Price"
              required
              error={touched.basicPrice && errors.basicPrice}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">₹</span>
                </div>
                <input
                  type="number"
                  min={0}
                  placeholder="Enter Basic Price"
                  className={`block w-full pl-8 p-2 border rounded ${
                    touched.basicPrice && errors.basicPrice
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  value={form.basicPrice || ""}
                  onChange={(e) => handleChange("basicPrice", e.target.value)}
                  onBlur={() => handleBlur("basicPrice")}
                />
              </div>
            </FormField>
          </div>

          {/* Tax Section */}
          <div className="mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">
                  Do you want to add separate service charges?
                </label>
                <div className="flex gap-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="yes"
                      checked={form.taxable === "yes"}
                      onChange={(e) => handleChange("taxable", e.target.value)}
                      className="form-radio h-4 w-4 text-blue-600"
                    />
                    <span className="ml-2">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="no"
                      checked={form.taxable === "no" || !form.taxable}
                      onChange={(e) => handleChange("taxable", e.target.value)}
                      className="form-radio h-4 w-4 text-blue-600"
                    />
                    <span className="ml-2">No</span>
                  </label>
                </div>
              </div>

              {form.taxable === "yes" && (
                <div>
                  <FormField
                    label="Service Charges (%)"
                    required={form.taxable === "yes"}
                    error={touched.taxPercentage && errors.taxPercentage}
                  >
                    <input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Enter Service Charges % (e.g., 5 or 18)"
                      className={`block w-full p-2 border rounded ${
                        touched.taxPercentage && errors.taxPercentage
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      value={form.taxPercentage || ""}
                      onChange={(e) =>
                        handleChange("taxPercentage", e.target.value)
                      }
                      onBlur={() => handleBlur("taxPercentage")}
                    />
                  </FormField>
                </div>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mt-4">
            <h3 className="text-lg font-medium mb-3">Additional Information</h3>
            <div className="space-y-4">
              <FormField label="Description">
                <textarea
                  value={form.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="p-2 w-full min-h-[120px] border border-gray-300 rounded shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  placeholder="Enter a detailed description of the service..."
                />
              </FormField>

              <FormField label="Images">
                <FileUpload
                  uploadEndpoint="/upload/fileUpload"
                  acceptedTypes={[
                    "image/jpeg",
                    "image/jpg",
                    "image/png",
                    "image/gif",
                    "image/webp",
                    "image/avif",
                    "image/heic",
                    "image/heif",
                    "image/svg+xml",
                  ]}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif,image/heic,image/heif,image/svg+xml"
                  multiple={true}
                  maxFiles={10}
                  moduleName="vendors"
                  docType="service"
                  existingUrls={form.images || []}
                  onUploadComplete={(urls) => {
                    setForm((prev) => ({ ...prev, images: urls }));
                  }}
                />
              </FormField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
 
export default React.memo(ServiceForm);
 